/** Reguły rozstrzygające, czy urządzeniu WOLNO jeszcze sprzedawać bez sieci:
 * wiek pakietu taryfowego, ważność puli numerów i limity sprzedaży wspólne dla
 * wielu kanałów. Moduł celowo BEZ ŻADNYCH IMPORTÓW — to czyste funkcje, więc
 * dają się sprawdzić testem bez IndexedDB, WebCrypto i sieci (bez nich reszta
 * kasy w ogóle się nie uruchamia, a te reguły decydują o pieniądzach).
 *
 * Zasada wspólna dla całego modułu: każda odmowa NAZYWA powód i mówi, co zrobić.
 * Martwy przycisk albo cicha nieobecność oferty są dla kasjera nie do odróżnienia
 * od awarii — a kasjer stoi przy pasażerze i musi wiedzieć, czy pomoże połączenie
 * z siecią, czy nie pomoże nic. */

// ── 1. wiek pakietu taryfowego (próg Zamawiającego) ──────────────────────────

export interface OfflinePolicy {
  /** próg wieku kompilatu w godzinach; brak/null = bez progu (stan sprzed 31.20) */
  bundle_max_age_hours?: number | null;
}

export type FreshnessState = "ok" | "warn" | "blocked" | "unknown";

export interface Freshness {
  ageHours: number | null;
  maxAgeHours: number | null;
  state: FreshnessState;
  /** zdanie dla kasjera — null tylko przy `ok` */
  reason: string | null;
}

/** Ostrzegamy, zanim pakiet przeterminuje się w środku zmiany — kasjer ma szansę
 * zsynchronizować urządzenie, póki jeszcze wolno sprzedawać. */
const WARN_AT = 0.8;

const hoursText = (h: number): string =>
  h < 10 ? `${h.toFixed(1).replace(".", ",")} h` : `${Math.round(h)} h`;

export function bundleFreshness(
  compiledAt: string | null | undefined,
  policy: OfflinePolicy | null | undefined,
  now: Date,
): Freshness {
  const max = policy?.bundle_max_age_hours ?? null;
  const limit = typeof max === "number" && max > 0 ? max : null;
  if (!compiledAt)
    // Brak pakietu to inna sprawa niż pakiet przeterminowany i ma własny
    // komunikat u wołającego („brak kompilatu taryfy"); tu tylko nie udajemy,
    // że znamy wiek.
    return { ageHours: null, maxAgeHours: limit, state: "unknown",
      reason: "brak pakietu taryfowego — zsynchronizuj urządzenie online" };
  const stamp = Date.parse(compiledAt);
  if (Number.isNaN(stamp)) {
    // Nieczytelnego znacznika nie zgadujemy. Bez progu nic to nie zmienia
    // (zachowanie sprzed wprowadzenia progu), z progiem — nie da się dowieść
    // świeżości, więc sprzedaż bez sieci stoi.
    return {
      ageHours: null, maxAgeHours: limit,
      state: limit === null ? "unknown" : "blocked",
      reason: "nieczytelny znacznik kompilacji pakietu taryfowego — "
        + "zsynchronizuj urządzenie online",
    };
  }
  const ageHours = Math.max(0, (now.getTime() - stamp) / 3_600_000);
  if (limit === null) return { ageHours, maxAgeHours: null, state: "ok", reason: null };
  if (ageHours > limit)
    return {
      ageHours, maxAgeHours: limit, state: "blocked",
      reason: `pakiet taryfowy ma ${hoursText(ageHours)} przy dopuszczalnych `
        + `${hoursText(limit)} — sprzedaż bez dostępu do sieci jest wstrzymana `
        + "do czasu synchronizacji urządzenia",
    };
  if (ageHours >= limit * WARN_AT)
    return {
      ageHours, maxAgeHours: limit, state: "warn",
      reason: `pakiet taryfowy ma ${hoursText(ageHours)} z dopuszczalnych `
        + `${hoursText(limit)} — zsynchronizuj urządzenie`,
    };
  return { ageHours, maxAgeHours: limit, state: "ok", reason: null };
}

// ── 2. pula numerów urządzenia: termin ważności i unieważnienie ──────────────

export interface DevicePool {
  series: string; from: number; to: number; next: number;
  /** termin ważności zakresu (ISO) — po nim numery nie wejdą do rozliczeń */
  expires_at?: string | null;
  /** moment unieważnienia zakresu przez operatora (ISO) */
  revoked_at?: string | null;
}

export interface PoolCheck {
  ok: boolean;
  reason: string | null;
  /** ile numerów zostało do końca zakresu (0 gdy pula nieużywalna) */
  remaining: number;
}

const dayText = (iso: string): string => iso.slice(0, 16).replace("T", " ");

/** Czy z tej puli wolno jeszcze wydać numer. Termin i unieważnienie sprawdzamy
 * PRZED wyczerpaniem, bo to różne komunikaty: wyczerpana pula to zwykła kolej
 * rzeczy, unieważniona znaczy, że sprzedaży i tak nikt nie przyjmie. */
export function poolCheck(
  pool: DevicePool | null | undefined, now: Date,
): PoolCheck {
  if (!pool)
    return { ok: false, remaining: 0,
      reason: "brak puli numerów — połącz się z SaleLight po zakres" };
  const zakres = `${pool.series} ${pool.from}–${pool.to}`;
  const remaining = Math.max(0, pool.to - pool.next + 1);
  if (pool.revoked_at && Date.parse(pool.revoked_at) <= now.getTime())
    return { ok: false, remaining: 0,
      reason: `pula numerów ${zakres} została unieważniona `
        + `(${dayText(pool.revoked_at)}) — sprzedaż z tych numerów nie zostanie `
        + "przyjęta przy synchronizacji; połącz się po nowy zakres" };
  if (pool.expires_at && Date.parse(pool.expires_at) <= now.getTime())
    return { ok: false, remaining: 0,
      reason: `pula numerów ${zakres} straciła ważność `
        + `(termin ${dayText(pool.expires_at)}) — sprzedaż z tych numerów nie `
        + "zostanie przyjęta przy synchronizacji; połącz się po nowy zakres" };
  if (remaining <= 0)
    return { ok: false, remaining: 0,
      reason: "pula numerów wyczerpana — połącz się z SaleLight po nowy zakres" };
  return { ok: true, reason: null, remaining };
}

// ── 3. limity sprzedaży i przydziały urządzenia ──────────────────────────────

/** Definicja limitu przeniesiona do pakietu taryfowego. Wymiary są te same, co
 * w `salelight/limits.py`; pusty wymiar znaczy „dowolny", a dopasowanie jest
 * KONIUNKCJĄ — reguła „wygrywa najbardziej szczegółowy" pozwalałaby obejść
 * limit ogólny dopisaniem szczegółowego. */
export interface SaleLimitRule {
  id: string;
  name: string;
  kind: "OFFER" | "RESOURCE";
  code: string | null;
  train_number: string | null;
  date_from: string | null;
  date_to: string | null;
  from_uic: string | null;
  to_uic: string | null;
  /** limit obejmuje więcej niż jeden kanał sprzedaży — offline bez przydziału
   * nie wolno go ruszyć, bo urządzenie nie widzi, ile zdjęły inne kanały */
  shared: boolean;
}

/** Rozłączna część limitu przydzielona TEMU urządzeniu na wskazany okres.
 * `used` jest licznikiem LOKALNYM: rośnie przy sprzedaży offline i wraca ze
 * stanem z serwera dopiero po synchronizacji. */
export interface LimitAllocation {
  limit_id: string;
  limit_name: string;
  capacity: number;
  used: number;
  valid_from: string | null;
  valid_until: string | null;
  revoked_at: string | null;
}

/** Kontekst sprzedaży potrzebny do dopasowania limitów. */
export interface LimitContext {
  offerCode: string;
  /** liczba biletów — przejazd liczy się biletami, jak `_limit_subjects` online */
  units: number;
  trainNumber?: string | null;
  /** dzień PRZEJAZDU (YYYY-MM-DD); null = nieznany */
  travelDay?: string | null;
  fromUic?: string | null;
  toUic?: string | null;
  now: Date;
}

const inRange = (day: string | null | undefined,
                 from: string | null, to: string | null): boolean => {
  if (!from && !to) return true;
  // Dzień przejazdu NIEZNANY przy limicie z zakresem dat: nie da się dowieść, że
  // sprzedaż jest poza zakresem, więc limit uznajemy za obowiązujący. Odwrotny
  // wybór przepuszczałby sprzedaż ponad limit po prostu przez pominięcie daty.
  if (!day) return true;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
};

const segmentApplies = (lim: SaleLimitRule,
                        fromUic: string | null | undefined,
                        toUic: string | null | undefined): boolean => {
  if (!lim.from_uic || !lim.to_uic) return true;
  if (!fromUic || !toUic) return false;
  // Urządzenie nie ma rozkładu, więc geografii nie zgadujemy: limit odcinkowy
  // obowiązuje przy DOSŁOWNIE tej relacji — ten sam przypadek, co online bez
  // zaimportowanego rozkładu (`limits.segment_applies`, sytuacja 3).
  return (fromUic === lim.from_uic && toUic === lim.to_uic)
    || (fromUic === lim.to_uic && toUic === lim.from_uic);
};

/** Limity obowiązujące tę sprzedaż (koniunkcja wypełnionych wymiarów). */
export function matchingLimits(
  limits: SaleLimitRule[] | null | undefined, ctx: LimitContext,
): SaleLimitRule[] {
  return (limits ?? []).filter((lim) =>
    lim.kind === "OFFER"
    && (!lim.code || lim.code === ctx.offerCode)
    && (!lim.train_number || lim.train_number === (ctx.trainNumber ?? null))
    && inRange(ctx.travelDay, lim.date_from, lim.date_to)
    && segmentApplies(lim, ctx.fromUic, ctx.toUic));
}

const allocationUsable = (
  a: LimitAllocation, now: Date,
): string | null => {
  if (a.revoked_at && Date.parse(a.revoked_at) <= now.getTime())
    return `przydział limitu „${a.limit_name}" został unieważniony `
      + `(${dayText(a.revoked_at)})`;
  if (a.valid_from && now.getTime() < Date.parse(a.valid_from))
    return `przydział limitu „${a.limit_name}" obowiązuje od `
      + `${dayText(a.valid_from)}`;
  if (a.valid_until && now.getTime() > Date.parse(a.valid_until))
    return `przydział limitu „${a.limit_name}" obowiązywał do `
      + `${dayText(a.valid_until)}`;
  return null;
};

export interface LimitDecision {
  /** powód odmowy — null gdy sprzedaż offline jest dopuszczalna */
  reason: string | null;
  /** przydziały do pomniejszenia po udanej sprzedaży: id limitu → jednostki */
  holds: { limit_id: string; units: number }[];
}

/** Czy tę ofertę wolno sprzedać bez sieci wobec limitów i przydziałów.
 *
 * Rozstrzygnięcia:
 * - limit z PRZYDZIAŁEM obowiązuje zawsze (urządzenie ma rozłączną część, więc
 *   umie ją policzyć samo) — wyczerpanie przydziału to odmowa;
 * - limit WSPÓLNY bez przydziału to odmowa, bo urządzenie nie wie, ile zdjęły
 *   inne kanały; sprzedaż tej oferty wymaga łączności;
 * - limit wyłącznie tego kanału i bez przydziału zostawiamy sprzedaży online
 *   (offline nie ma czego naruszyć wobec innych kanałów) — wymóg 31.6 dotyczy
 *   limitu WSPÓLNEGO i nie rozciągamy go na własny bez decyzji przewoźnika. */
export function limitDecision(
  limits: SaleLimitRule[] | null | undefined,
  allocations: LimitAllocation[] | null | undefined,
  ctx: LimitContext,
): LimitDecision {
  const holds: { limit_id: string; units: number }[] = [];
  const units = Math.max(1, ctx.units);
  for (const lim of matchingLimits(limits, ctx)) {
    const alloc = (allocations ?? []).find((a) => a.limit_id === lim.id);
    if (!alloc) {
      if (!lim.shared) continue;
      return { holds: [],
        reason: `oferta jest objęta limitem wspólnym „${lim.name}", a urządzenie `
          + "nie ma przydzielonej części tego limitu — sprzedaż tej oferty "
          + "wymaga łączności" };
    }
    const blokada = allocationUsable(alloc, ctx.now);
    if (blokada)
      return { holds: [], reason: `${blokada} — sprzedaż tej oferty wymaga łączności` };
    if (alloc.used + units > alloc.capacity)
      return { holds: [],
        reason: `przydzielona urządzeniu część limitu „${lim.name}" jest `
          + `wyczerpana (${alloc.used} z ${alloc.capacity}) — sprzedaż tej oferty `
          + "wymaga łączności" };
    holds.push({ limit_id: lim.id, units });
  }
  return { reason: null, holds };
}

/** Pomniejszenie przydziałów po sprzedaży (nowa tablica — stanu nie mutujemy,
 * żeby nieudany zapis nie zostawiał zjedzonego licznika w pamięci). */
export function consumeAllocations(
  allocations: LimitAllocation[] | null | undefined,
  holds: { limit_id: string; units: number }[],
): LimitAllocation[] {
  const byId = new Map(holds.map((h) => [h.limit_id, h.units]));
  return (allocations ?? []).map((a) =>
    byId.has(a.limit_id) ? { ...a, used: a.used + byId.get(a.limit_id)! } : a);
}

// ── 4. paczka odrzucona przy synchronizacji (tryb uzgodnienia) ───────────────

export interface Rejection {
  at: string;
  reason: string;
  /** ile transakcji objęła odrzucona paczka — bilety są już u podróżnych */
  transactions: number;
}

/** Zdanie dla kasjera o odrzuconej paczce. Odrzucenie NIE znaczy, że sprzedaży
 * nie było: bilety zostały wydane, więc transakcje zostają w magazynie i czekają
 * na uzgodnienie u operatora, zamiast zniknąć razem z komunikatem błędu. */
export function rejectionNotice(r: Rejection | null | undefined): string | null {
  if (!r) return null;
  return `Paczka ${r.transactions} transakcji odrzucona przy synchronizacji `
    + `(${dayText(r.at)}): ${r.reason}. Bilety zostały już wydane podróżnym — `
    + "transakcje czekają na urządzeniu do uzgodnienia z operatorem.";
}
